import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft } from '@phosphor-icons/react';
import { applySeo, CANONICAL_ORIGIN } from '../lib/seo';
import { BlogPdfBox } from '../components/BlogPdfBox';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

// /blog/:slug — public article page with SEO meta + Article JSON-LD.
export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setNotFound(false);
    axios
      .get(`${API}/api/blog/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!post) return undefined;
    return applySeo({
      title: `${post.title} — SalesCrunchCRM`,
      description: post.description,
      path: `/blog/${post.slug}`,
      type: 'article',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description: post.description,
        datePublished: post.date,
        dateModified: post.date,
        mainEntityOfPage: `${CANONICAL_ORIGIN}/blog/${post.slug}`,
        image: `${CANONICAL_ORIGIN}/og-default.svg`,
        author: { '@type': 'Organization', name: 'SalesCrunchCRM', url: CANONICAL_ORIGIN },
        publisher: { '@type': 'Organization', name: 'SalesCrunchCRM', url: CANONICAL_ORIGIN },
      },
    });
  }, [post]);

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="blog-post-page">
      <header className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-black text-slate-900 hover:text-[#0B5394]" data-testid="blog-post-home-link">
            SalesCrunch<span className="text-[#0B5394]">CRM</span>
          </Link>
          <Link
            to="/login"
            className="text-sm font-bold text-white bg-[#0B5394] hover:bg-[#094276] px-4 py-2 rounded"
            data-testid="blog-post-top-cta"
          >
            Try it — $30/user/mo
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0B5394] hover:underline mb-8"
          data-testid="blog-post-back-link"
        >
          <ArrowLeft size={14} weight="bold" /> All essays
        </Link>

        {notFound && (
          <div className="py-16 text-center" data-testid="blog-post-not-found">
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-4">Essay not found</h1>
            <p className="text-slate-500 mb-6">That link doesn&rsquo;t match anything we&rsquo;ve published.</p>
            <Link to="/blog" className="text-sm font-bold text-white bg-[#0B5394] hover:bg-[#094276] px-5 py-2.5 rounded">
              Browse all essays
            </Link>
          </div>
        )}

        {!notFound && !post && <p className="py-16 text-slate-400">Loading&hellip;</p>}

        {post && (
          <article>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#CC6600] mb-3" data-testid="blog-post-series">
              {post.series}
            </p>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 leading-tight mb-4" data-testid="blog-post-title">
              {post.title}
            </h1>
            <p className="text-sm text-slate-400 mb-10" data-testid="blog-post-meta">
              {fmtDate(post.date)} &middot; {post.read_minutes} min read &middot; SalesCrunchCRM
            </p>

            <div className="blog-body" data-testid="blog-post-body">
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </div>

            <div className="mt-12">
              <BlogPdfBox />
            </div>

            <div className="mt-14 rounded-lg border border-slate-200 bg-slate-50 p-8 text-center" data-testid="blog-post-cta-card">
              {post.cta === 'videocrunch' ? (
                <>
                  <p className="text-xl font-black text-slate-900 mb-2">Point a camera at your money path.</p>
                  <p className="text-sm text-slate-500 mb-5">
                    VideoCrunch drives a real browser through your product and films it &mdash; tutorial videos that double as a test suite. Your first video is free.
                  </p>
                  <Link
                    to="/videocrunch"
                    className="inline-block text-sm font-bold text-white bg-[#0B5394] hover:bg-[#094276] px-5 py-2.5 rounded"
                    data-testid="blog-post-cta-btn"
                  >
                    See VideoCrunch &mdash; first video free
                  </Link>
                </>
              ) : post.cta === 'migrate' ? (
                <>
                  <p className="text-xl font-black text-slate-900 mb-2">Leave the bloat. Take every note with you.</p>
                  <p className="text-sm text-slate-500 mb-5">
                    Patent-pending AI migration &mdash; fields mapped automatically, every note kept with its contact. Minutes, not weeks.
                  </p>
                  <Link
                    to="/migrate"
                    className="inline-block text-sm font-bold text-white bg-[#0B5394] hover:bg-[#094276] px-5 py-2.5 rounded"
                    data-testid="blog-post-cta-btn"
                  >
                    Try MigrationCrunch free &mdash; first 50 contacts
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-xl font-black text-slate-900 mb-2">Remember everything. Miss nothing. No training required.</p>
                  <p className="text-sm text-slate-500 mb-5">
                    No pipelines, no scores, no stage gates. Your notes, an alarm that fires on the right day, and an AI that briefs you before every call. $30 per user per month.
                  </p>
                  <Link
                    to="/signup"
                    className="inline-block text-sm font-bold text-white bg-[#0B5394] hover:bg-[#094276] px-5 py-2.5 rounded"
                    data-testid="blog-post-cta-btn"
                  >
                    Try SalesCrunchCRM &mdash; $30/user/mo
                  </Link>
                </>
              )}
            </div>

            <p className="mt-6 text-center text-xs text-slate-400" data-testid="blog-post-comparison-link">
              Shopping around? See the honest side-by-side of Salesforce, HubSpot, ACT!, Zoho and Pipedrive at{' '}
              <a href="https://crmcomparisons.com" target="_blank" rel="noopener" className="font-bold text-[#0B5394] hover:underline">crmcomparisons.com</a>
            </p>

            <div className="mt-10 text-center">
              <Link to="/blog" className="text-sm font-bold text-[#0B5394] hover:underline" data-testid="blog-post-bottom-back">
                &larr; Read the rest of the series
              </Link>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
